import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { RotateCcw, AlertTriangle, CheckCircle, Search, X } from 'lucide-react';
import api from '../../../services/api';
import studyService from '../../../services/studyService';
import siteService from '../../../services/siteService';
import participantService from '../../../services/participantService';

const IDENTIFIER_MODES = [
  { value: 'email', label: 'Email' },
  { value: 'participant_id', label: 'Participant ID' },
  { value: 'participant_uuid', label: 'UUID' },
];

const ParticipantResetTool = () => {
  const [idMode, setIdMode] = useState('participant_id');
  const [identifier, setIdentifier] = useState('');
  const [survey, setSurvey] = useState('');
  const [keepHistory, setKeepHistory] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  // Cascading picker state
  const [studies, setStudies] = useState([]);
  const [sites, setSites] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [studyId, setStudyId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantFilter, setParticipantFilter] = useState('');
  const [loadingStudies, setLoadingStudies] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingStudies(true);
      try {
        const response = await studyService.getStudies();
        if (cancelled) return;
        const list = Array.isArray(response) ? response : [];
        setStudies(list.filter(s => s && s.id));
      } catch (err) {
        if (!cancelled) toast.error('Could not load studies');
      } finally {
        if (!cancelled) setLoadingStudies(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setSites([]);
    setSiteId('');
    setParticipants([]);
    setSelectedParticipant(null);
    if (!studyId) return;
    let cancelled = false;
    const load = async () => {
      setLoadingSites(true);
      try {
        const list = await siteService.getSitesForStudy(studyId);
        if (cancelled) return;
        setSites(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) toast.error('Could not load sites');
      } finally {
        if (!cancelled) setLoadingSites(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [studyId]);

  useEffect(() => {
    setParticipants([]);
    setSelectedParticipant(null);
    setParticipantFilter('');
    if (!studyId || !siteId) return;
    let cancelled = false;
    const load = async () => {
      setLoadingParticipants(true);
      try {
        const list = await participantService.getParticipantsForSite(studyId, siteId);
        if (cancelled) return;
        setParticipants(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) toast.error('Could not load participants');
      } finally {
        if (!cancelled) setLoadingParticipants(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [studyId, siteId]);

  const choosePickerParticipant = (participant) => {
    setSelectedParticipant(participant);
    if (participant?.participant_id) {
      setIdMode('participant_id');
      setIdentifier(participant.participant_id);
    } else if (participant?.id) {
      setIdMode('participant_id');
      setIdentifier(String(participant.id));
    }
  };

  const clearPicker = () => {
    setStudyId('');
    setSiteId('');
    setSelectedParticipant(null);
    setParticipantFilter('');
  };

  const filteredParticipants = participants.filter(p => {
    if (!participantFilter.trim()) return true;
    const needle = participantFilter.trim().toLowerCase();
    return [
      p.participant_id,
      p.participant_number,
      p.email,
      String(p.id ?? ''),
    ]
      .filter(Boolean)
      .some(v => String(v).toLowerCase().includes(needle));
  });

  const runReset = async () => {
    if (!identifier.trim()) {
      toast.error(`Enter a ${IDENTIFIER_MODES.find(m => m.value === idMode).label}`);
      return;
    }

    setLoading(true);
    setSummary(null);

    try {
      const body = {
        [idMode]: identifier.trim(),
        survey: survey.trim() || undefined,
        keep_history: keepHistory,
        dry_run: dryRun,
      };
      const { data } = await api.post('/api/dev/reset-participant-assignments/', body);
      setSummary(data);
      toast.success(
        data.dry_run
          ? 'Dry run complete — nothing was changed'
          : 'Reset complete'
      );
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Reset failed: ${detail}`);
      setSummary({ error: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          <RotateCcw className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          Reset Test Participant
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Reopens completed survey windows, resets assignments, and (by default) deletes
          response history so a test participant can retake assessments immediately.
          Blocked in production.
        </p>
      </div>

      <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/70 p-4 dark:border-gray-700 dark:bg-gray-900/30">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <Search className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            Find participant
          </h4>
          {(studyId || siteId || selectedParticipant) && (
            <button
              type="button"
              onClick={clearPicker}
              className="btn btn-ghost btn-sm"
            >
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">
              Study {loadingStudies && <span className="text-gray-400">(loading…)</span>}
            </label>
            <select
              value={studyId}
              onChange={(e) => setStudyId(e.target.value)}
              disabled={loadingStudies}
              className="input"
            >
              <option value="">— Select study —</option>
              {studies.map(s => (
                <option key={s.id} value={s.id}>
                  {s.title || s.study_name || `Study ${s.id}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">
              Site {loadingSites && <span className="text-gray-400">(loading…)</span>}
            </label>
            <select
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              disabled={!studyId || loadingSites}
              className="input"
            >
              <option value="">{studyId ? '— Select site —' : 'Pick study first'}</option>
              {sites.map(s => (
                <option key={s.id} value={s.id}>
                  {s.site_name || s.name || `Site ${s.id}`}
                  {s.site_code ? ` (${s.site_code})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {studyId && siteId && (
          <div>
            <label className="label">
              Participant {loadingParticipants && <span className="text-gray-400">(loading…)</span>}
              {!loadingParticipants && participants.length > 0 && (
                <span className="text-gray-400"> ({filteredParticipants.length} of {participants.length})</span>
              )}
            </label>
            <input
              type="text"
              value={participantFilter}
              onChange={(e) => setParticipantFilter(e.target.value)}
              placeholder="Filter by participant ID, number, or email…"
              className="input mb-2"
            />
            <div className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
              {loadingParticipants ? (
                <div className="p-3 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
              ) : filteredParticipants.length === 0 ? (
                <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
                  {participants.length === 0 ? 'No participants at this site.' : 'No matches.'}
                </div>
              ) : (
                filteredParticipants.map(p => {
                  const isSelected = selectedParticipant?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => choosePickerParticipant(p)}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors hover:bg-primary-50 dark:hover:bg-primary-900/20 ${
                        isSelected ? 'bg-primary-100 dark:bg-primary-900/40' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-mono text-gray-900 dark:text-gray-100 truncate">
                            {p.participant_id || `#${p.id}`}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            #{p.participant_number ?? '—'} · {p.site?.site_name || '—'}
                            {p.last_login && ` · last login ${new Date(p.last_login).toLocaleDateString()}`}
                          </div>
                        </div>
                        <span className={`badge flex-shrink-0 ${
                          p.is_active
                            ? 'badge-success'
                            : 'badge-gray'
                        }`}>
                          {p.is_active ? 'active' : 'inactive'}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {selectedParticipant && (
          <div className="flex items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-700 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
            <CheckCircle className="h-4 w-4" />
            Will reset <span className="font-mono">{selectedParticipant.participant_id || `#${selectedParticipant.id}`}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="label">
            Identify by
          </label>
          <select
            value={idMode}
            onChange={(e) => setIdMode(e.target.value)}
            className="input"
          >
            {IDENTIFIER_MODES.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="label">
            {IDENTIFIER_MODES.find(m => m.value === idMode).label}
          </label>
          <input
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder={idMode === 'email' ? 'jake+test@example.com' : idMode === 'participant_id' ? '9999-1818' : 'uuid'}
            className="input"
          />
        </div>
      </div>

      <div>
        <label className="label">
          Survey filter (optional)
        </label>
        <input
          type="text"
          value={survey}
          onChange={(e) => setSurvey(e.target.value)}
          placeholder="Survey id or name. Leave blank to reset every survey for the participant."
          className="input"
        />
      </div>

      <div className="flex flex-wrap gap-6">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            className="h-4 w-4"
          />
          Dry run (preview, no changes)
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={keepHistory}
            onChange={(e) => setKeepHistory(e.target.checked)}
            className="h-4 w-4"
          />
          Keep history (preserve CompletedSurveys & responses)
        </label>
      </div>

      {!dryRun && (
        <div className="flex items-start gap-2.5 rounded-lg border border-warning-500/30 bg-warning-50 px-4 py-3 dark:bg-warning-500/10">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning-600 dark:text-warning-400" />
          <div className="text-sm text-warning-800 dark:text-warning-400">
            <strong>Dry run is off.</strong> Running will delete CompletedSurvey and
            ParticipantResponse rows (unless Keep history is checked).
          </div>
        </div>
      )}

      <button
        onClick={runReset}
        disabled={loading}
        className="btn btn-primary"
      >
        {loading ? 'Running…' : (dryRun ? 'Preview reset' : 'Run reset')}
      </button>

      {summary && !summary.error && (
        <div className="mt-4 space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <CheckCircle className="h-4 w-4 text-success-600 dark:text-success-400" />
            {summary.dry_run ? 'Dry run — no changes made' : 'Reset complete'}
          </div>
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <div>
              <span className="font-mono">{summary.participant.email}</span>{' '}
              <span className="text-gray-500 dark:text-gray-400">
                ({summary.participant.participant_id}, {summary.participant.uuid})
              </span>
            </div>
            {summary.survey_filter && (
              <div>Scope: <span className="font-mono">{summary.survey_filter}</span></div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
            <Counter label="Windows" value={summary.counts.windows} />
            <Counter label="Assignments" value={summary.counts.assignments} />
            <Counter
              label="Completed surveys"
              value={summary.counts.completed_surveys}
              sub={summary.keep_history ? 'preserved' : 'deleted'}
            />
            <Counter
              label="Responses"
              value={summary.counts.responses}
              sub={summary.keep_history ? 'preserved' : 'deleted'}
            />
          </div>
          {!summary.dry_run && (
            <div className="pt-2 text-sm text-gray-600 dark:text-gray-400">
              Windows: {summary.window_statuses.open} open,{' '}
              {summary.window_statuses.pending} pending,{' '}
              {summary.window_statuses.closed} closed (past end_time)
            </div>
          )}
        </div>
      )}

      {summary?.error && (
        <div className="mt-4 rounded-lg border border-danger-500/25 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:bg-danger-500/10 dark:text-danger-400">
          {summary.error}
        </div>
      )}
    </div>
  );
};

const Counter = ({ label, value, sub }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
    <div className="stat-label">{label}</div>
    <div className="stat-value">{value}</div>
    {sub && <div className="text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
  </div>
);

export default ParticipantResetTool;
